import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Heart, Truck, ArrowsClockwise, ShieldCheck, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import StarRating from "../components/StarRating";
import FitGuideModal from "../components/FitGuideModal";
import ReviewsSection from "../components/ReviewsSection";
import WellnessProductExtras from "../components/WellnessProductExtras";
import GiftAddOn from "../components/GiftAddOn";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [openAcc, setOpenAcc] = useState("details");
  const [fitOpen, setFitOpen] = useState(false);
  const [giftAddon, setGiftAddon] = useState(null);
  const { addItem } = useCart();
  const { user, setUser } = useAuth();

  useEffect(() => {
    api.get(`/products/${slug}`).then(r => {
      setProduct(r.data);
      setColor(r.data.colors?.[0] || "");
    });
  }, [slug]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-[#5C5C5C]" data-testid="product-loading">Loading...</div>;

  const liked = user?.wishlist?.includes(product.id);

  const toggleWishlist = async () => {
    if (!user) return toast.error("Please sign in to save favourites");
    const { data } = await api.post("/wishlist/toggle", { product_id: product.id });
    setUser({ ...user, wishlist: data.wishlist });
  };

  const handleAdd = () => {
    if (!size) return toast.error("Please select a size");
    addItem(product, size, color);
    if (giftAddon && product.slug === "reconnect-ritual") {
      addItem(giftAddon.product, giftAddon.size, giftAddon.color, 1, {
        is_gift: true,
        gift_note: giftAddon.note,
        gift_parent: product.slug,
        gift_recipient_name: giftAddon.recipientName,
        gift_recipient_email: giftAddon.recipientEmail,
      });
      toast.success(`${product.name} + ${giftAddon.product.name} added as a gift`);
    } else {
      toast.success(`${product.name} added to bag`);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 lg:py-16" data-testid="product-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Image gallery */}
        <div className="lg:col-span-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.images.map((src, i) => (
              <img key={i} src={src} alt={`${product.name} ₹{i+1}`} className="w-full aspect-[3/4] object-cover" data-testid={`product-image-${i}`} />
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
          <div className="overline text-[#4A1525] mb-4">{product.category}</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-3">{product.name}</h1>
          {product.review_count > 0 && (
            <div className="mb-4"><StarRating value={product.avg_rating} count={product.review_count} showValue size={13} /></div>
          )}
          <div className="text-xl mb-8">₹{product.price.toFixed(0)}</div>
          <p className="text-[#5C5C5C] leading-relaxed mb-8">{product.description}</p>

          {product.colors?.length > 0 && (
            <div className="mb-8">
              <div className="overline mb-3">Colour <span className="text-[#5C5C5C]">— {color}</span></div>
              <div className="flex gap-3">
                {product.colors.map(c => (
                  <button key={c} onClick={() => setColor(c)} className={`px-4 py-2 border text-sm transition-colors ${color === c ? "border-[#4A1525] bg-[#4A1525] text-[#FAF8F5]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`} data-testid={`color-${c}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="flex justify-between overline mb-3">
              <span>{product.category === "reconnect" ? "Format" : "Size"}</span>
              {product.category !== "reconnect" && product.sizes.length > 1 && (
                <button type="button" onClick={() => setFitOpen(true)} className="underline underline-offset-4 text-[#5C5C5C] normal-case tracking-normal text-xs hover:text-[#4A1525]" data-testid="open-fit-guide">Fit guide</button>
              )}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {product.sizes.map(s => (
                <button key={s} onClick={() => setSize(s)} className={`py-3 border text-sm transition-colors ${size === s ? "border-[#4A1525] bg-[#4A1525] text-[#FAF8F5]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`} data-testid={`size-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button onClick={handleAdd} className="flex-1 bg-[#4A1525] text-[#FAF8F5] py-4 overline hover:bg-[#360F1B] transition-colors" data-testid="add-to-bag-btn">
              {giftAddon ? "Add ritual + gift to bag" : "Add to bag"}
            </button>
            <button onClick={toggleWishlist} className="w-14 h-14 border border-[#1A1A1A] flex items-center justify-center hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors" data-testid="wishlist-btn-detail" aria-label="Save">
              <Heart size={18} weight={liked ? "fill" : "light"} color={liked ? "#4A1525" : undefined} />
            </button>
          </div>

          {product.slug === "reconnect-ritual" && (
            <GiftAddOn onGiftChange={setGiftAddon} />
          )}

          {/* Perks */}
          <div className="grid grid-cols-3 gap-4 py-6 border-y border-[#E6DED5] text-center text-xs text-[#5C5C5C]">
            <div className="flex flex-col items-center gap-1"><Truck size={20} weight="light" /><span>Free ship ₹5,000+</span></div>
            <div className="flex flex-col items-center gap-1"><ArrowsClockwise size={20} weight="light" /><span>30-day returns</span></div>
            <div className="flex flex-col items-center gap-1"><ShieldCheck size={20} weight="light" /><span>Discreet delivery</span></div>
          </div>

          {/* Accordions */}
          <div className="mt-6 divide-y divide-[#E6DED5] border-b border-[#E6DED5]">
            {[
              { key: "details", title: "Details & Fit", body: "Adjustable straps · French seams · Hand-finished · Fits true to size. Model is 5'9\" wearing size S." },
              { key: "materials", title: "Materials", body: "82% Nylon, 18% Elastane. Delicate hand wash cold. Lay flat to dry." },
              { key: "shipping", title: "Shipping & Returns", body: "Discreet packaging always. Complimentary shipping on orders over ₹5,000. 30-day return window." },
            ].map(acc => (
              <div key={acc.key} className="py-4">
                <button onClick={() => setOpenAcc(openAcc === acc.key ? "" : acc.key)} className="w-full flex justify-between items-center overline" data-testid={`acc-${acc.key}`}>
                  {acc.title}
                  <CaretDown size={12} weight="light" className={`transition-transform ${openAcc === acc.key ? "rotate-180" : ""}`} />
                </button>
                {openAcc === acc.key && <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">{acc.body}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {product.category === "reconnect" && <WellnessProductExtras product={product} />}
      <ReviewsSection product={product} />
      <FitGuideModal open={fitOpen} onClose={() => setFitOpen(false)} />
    </div>
  );
}
